import { Localized, useLocalization } from "@fluent/react";
import * as Sentry from "@sentry/react";
import { ErrorScreen } from "@lending/components/ErrorScreen/ErrorScreen";
import { PageLayout } from "@lending/components/PageLayout/PageLayout";
import { useGetInstitutionQuery } from "@lending/hooks/queries/useGetInstitutionQuery";
import { OfferCard } from "./components/OfferCard/OfferCard";
import { useOfferContainer } from "./hooks/useOfferContainer";
import styles from "./Offers.module.css";

function Offers() {
  const { l10n } = useLocalization();
  const { institution } = useGetInstitutionQuery();
  const institutionName = institution.short_name ?? "";
  const ctaLabel = l10n.getString("ui-accept-offer", null, "Accept offer");
  const infoLinkUrl =
    institution.lending_credit_card_offer_info_and_disclaimer_url?.trim() ||
    institution.lending_offer_info_and_disclaimer_url?.trim() ||
    "";

  const {
    isLoading,
    isPending,
    primaryApplicantFirstName,
    coApplicantFirstName,
    offer,
    acceptOffer,
  } = useOfferContainer();

  if (!isLoading && !offer) {
    Sentry.captureMessage(
      "A user navigated to the credit card offer screen but did not have an offer. This should not happen."
    );
    return <ErrorScreen />;
  }

  return (
    <PageLayout isLoading={isLoading} className={styles.pageLayout}>
      <div className="alignChild--center--center">
        <div className={styles.content}>
          <div className={styles.header}>
            <p className={styles.subtitle}>
              {coApplicantFirstName
                ? l10n.getString(
                    "credit-card-offers-heading-joint",
                    {
                      firstName: primaryApplicantFirstName,
                      secondName: coApplicantFirstName,
                    },
                    `🎉 Congrats, ${primaryApplicantFirstName} and ${coApplicantFirstName}`
                  )
                : l10n.getString(
                    "credit-card-offers-heading",
                    { firstName: primaryApplicantFirstName },
                    `🎉 Congrats, ${primaryApplicantFirstName}`
                  )}
            </p>
            <h1 className={styles.title}>
              <Localized
                id="credit-card-offers-subheading"
                vars={{ offerName: offer?.product.name ?? "" }}
              >
                You&apos;re pre-approved:
              </Localized>{" "}
              <span className={styles.offerName}>{offer?.product.name}</span>
            </h1>
          </div>
          <div className={styles.body}>
            {offer && (
              <OfferCard
                offer={offer}
                expirationDays={institution.lending_application_offers_expiration_days}
                infoLinkUrl={infoLinkUrl}
                onPressAccept={acceptOffer}
                isPending={isPending}
              />
            )}
            <div className={styles.footer}>
              <div className="fontSize--xs fontWeight--thin">
                <Localized id="offers-consent-disclaimer-cc" vars={{ ctaLabel, institutionName }}>
                  {`By clicking "${ctaLabel}" above, you consent to authorize ${institutionName} to obtain and use Consumer Reports about you to review your application, to review the products or services you maintain, and to analyze your financial needs and to market other products and services to you.`}
                </Localized>
              </div>
            </div>
          </div>
        </div>
      </div>
    </PageLayout>
  );
}

export { Offers };
